import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { MuiThemeProvider } from '@material-ui/core';
import { getTheme } from 'core/styles/getTheme';
import _, { find, isEmpty } from 'lodash';
import queryString from 'query-string';
import { ILumaBotFlowTemplate, LoggerStatusEnum, sendLog, useLumaBotFlowTemplateManyV2 } from 'ui-model';

import { selectAppReady } from '@lib/selectors';
import { isAnyMobile } from '@lib/services/navigatorService';

import { useSound, useWindowMessage, UseWindowMessageCaller } from 'hooks';

import useLumaBotSettings from '../useLumaBotSettings';
import { ELumaBotFlowType } from '../WIP/ui-model/types';

import WidgetLumaBot, { WidgetLumaBotProps } from './WidgetLumaBot';
import WidgetLumaBotFab from './WidgetLumaBotFab';

const isMobile = isAnyMobile();

type Query = {
  mode?: 'read-only';
  showDeactivatedFlows?: 'true';
  widget?: 'fab' | 'popper';
  flow?: string;
  variant?: WidgetLumaBotProps['variant'];
  minutesKnockKnock?: string;
  minutesSessionTimeout?: string;
};

export interface WidgetLumaBotWrapperProps extends RouteComponentProps<any, any> {}

const WidgetLumaBotWrapper: React.FC<WidgetLumaBotWrapperProps> = ({ location }) => {
  const { theme: themeOptions } = useLumaBotSettings();
  const { playSound } = useSound();
  const appReady = useSelector(selectAppReady);
  const query = location.query as Query;
  const { widget, mode, flow: flowId, variant, minutesKnockKnock, minutesSessionTimeout, showDeactivatedFlows } = query;
  const fabMode = widget === 'fab';
  const readOnly = mode === 'read-only';
  const [open, setOpen] = useState(false);
  const messagesUnreadCountRef = useRef(0);

  const handleAction = (action: 'shutdown' | 'open' | 'close' | 'closePrompt' | 'changeTitle', value?: any) => {
    if (!window.parent) return null;

    if (action === 'close') {
      sessionStorage.setItem('widgetClosedByUser', 'true');
    }

    if (action === 'closePrompt') {
      sessionStorage.setItem('promptClosedByUser', 'true');
    }

    window.parent.postMessage({ caller: 'lh:lumabot', action, value }, '*');
  };

  // Fetch flow templates we want to expose
  const params = {
    ...(fabMode && { select: 'flowType' }),
    ...(!showDeactivatedFlows && { active: true }),
  };
  // FIXME Fix data not being an array for fetchMany query
  const { data: flowTemplatePagination, refetch } = useLumaBotFlowTemplateManyV2(params, {
    enabled: false,
    onSettled: (data, error) => {
      if (isEmpty(data) || !!error) {
        sendLog('LumaBot: No flows', { params, data, error }, LoggerStatusEnum.info);
        handleAction('shutdown');
      }
    },
  });
  const flowTemplates = useMemo(() => (_.flatten(flowTemplatePagination?.pages) as unknown) as ILumaBotFlowTemplate[], [
    flowTemplatePagination?.pages,
  ]);

  // Using the `refetch` approach to ensure we don't run the call multiple times
  useEffect(() => {
    if (appReady) refetch();
  }, [appReady, refetch]);

  const hasRequiredFlowTemplate = flowId
    ? find(flowTemplates, { _id: flowId })
    : find(flowTemplates, { flowType: ELumaBotFlowType.Welcome });

  // Handle LumaBot messages between iframes
  useWindowMessage((event) => {
    if (event.data.caller !== UseWindowMessageCaller.LumaBot) return;
    if (event.data.action === 'open') setOpen(true);
    if (event.data.action === 'close') setOpen(false);
  }, fabMode);

  return (
    <MuiThemeProvider theme={getTheme(themeOptions)}>
      {fabMode && !hasRequiredFlowTemplate && null}
      {fabMode && hasRequiredFlowTemplate && (
        // Fab mode
        <WidgetLumaBotFab
          readOnly={readOnly}
          onToggle={() => {
            if (open) handleAction('close');
            else {
              // Open the LumaBot with configured theme so we don't need to wait
              // for it before showing the loading spinner
              const openParams = { theme: JSON.stringify(themeOptions) };
              handleAction('open', queryString.stringify(openParams));
            }

            // WORKAROUND skip changing the status on mobile since it will be closed from another iframe
            if (!isMobile) setOpen(!open);
          }}
          onClosePrompt={() => handleAction('closePrompt')}
        />
      )}
      {!fabMode && (
        // Chat mode
        <WidgetLumaBot
          readOnly={readOnly}
          flowTemplates={flowTemplates}
          flowId={flowId}
          variant={variant}
          isPopper={widget === 'popper'}
          minutesKnockKnock={minutesKnockKnock ? parseFloat(minutesKnockKnock) : undefined}
          minutesSessionTimeout={minutesSessionTimeout ? parseFloat(minutesSessionTimeout) : undefined}
          onMessagesUnread={(count = 1) => {
            messagesUnreadCountRef.current += count;
            playSound();
            handleAction('changeTitle', `New message! (${messagesUnreadCountRef.current})`);
          }}
          onMessagesRead={() => {
            messagesUnreadCountRef.current = 0;
            handleAction('changeTitle', '');
          }}
          onClose={() => handleAction('close')}
        />
      )}
    </MuiThemeProvider>
  );
};

export default WidgetLumaBotWrapper;
